// src/hooks/useAttendanceStatus.js
import { useQuery } from '@tanstack/react-query';
import { supabase } from '@/lib/supabase';
import { isWeekend, parseISO } from 'date-fns';
import { formatLocalDate } from '@/lib/dateUtils';

// =====================================================
// ATTENDANCE STATUS CONSTANTS
// =====================================================

export const ATTENDANCE_STATUS = {
  PRESENT: 'present',
  ABSENT_PLANNED: 'absent_planned',
  ABSENT_UNPLANNED: 'absent_unplanned',
  ABSENT_CLOSURE: 'absent_closure',
  WEEKEND: null, // Don't show status for weekends (non-working days)
  FUTURE: null // Don't show status for future dates
};

export const ATTENDANCE_LABELS = {
  present: 'Obecny',
  absent_planned: 'Nieobecny (planowany)',
  absent_unplanned: 'Nieobecny',
  absent_closure: 'Zamknięte'
};

export const ATTENDANCE_COLORS = {
  present: {
    bg: 'bg-green-500',
    hover: 'hover:bg-green-600',
    text: 'text-white'
  },
  absent_planned: {
    bg: 'bg-amber-500',
    hover: 'hover:bg-amber-600',
    text: 'text-white'
  },
  absent_unplanned: {
    bg: 'bg-red-500',
    hover: 'hover:bg-red-600',
    text: 'text-white'
  },
  absent_closure: {
    bg: 'bg-red-600',
    hover: '',
    text: 'text-white'
  }
};

// =====================================================
// HELPER FUNCTIONS
// =====================================================

/**
 * Derive attendance status from daily_update and absence records
 *
 * Priority:
 * 1. Future dates -> null (don't show)
 * 2. Weekends -> null (non-working days, don't show)
 * 3. Closure absence (type='closure') -> 'absent_closure' (cannot be overridden)
 * 4. Teacher marked present (attendance=true) -> 'present' (overrides absence)
 * 5. Absence record exists -> 'absent_planned' or 'absent_unplanned'
 * 6. Default -> 'present' (opt-out model)
 *
 * @param {string} date - The date to check (YYYY-MM-DD)
 * @param {object|null} dailyUpdate - Daily update record for the child
 * @param {object|null} absence - Absence record for the child on this date
 * @returns {string|null} Attendance status or null for future/weekend dates
 */
export function getAttendanceStatus(date, dailyUpdate, absence) {
  const today = formatLocalDate(new Date());

  // Don't show status for future dates
  if (date > today) {
    return ATTENDANCE_STATUS.FUTURE;
  }

  // Don't show status for weekends (non-working days)
  if (isWeekend(parseISO(date))) {
    return ATTENDANCE_STATUS.WEEKEND;
  }

  // Closure absences cannot be overridden by teacher
  if (absence?.type === 'closure') {
    return ATTENDANCE_STATUS.ABSENT_CLOSURE;
  }

  // Teacher explicitly marked present - this overrides any absence record
  if (dailyUpdate?.attendance === true) {
    return ATTENDANCE_STATUS.PRESENT;
  }

  // Teacher explicitly marked absent (old binary system) - treat as unplanned
  if (dailyUpdate?.attendance === false) {
    return ATTENDANCE_STATUS.ABSENT_UNPLANNED;
  }

  // Check for absence record (parent-reported or other)
  if (absence) {
    return absence.is_planned
      ? ATTENDANCE_STATUS.ABSENT_PLANNED
      : ATTENDANCE_STATUS.ABSENT_UNPLANNED;
  }

  // Default: present (opt-out model - children are present unless marked otherwise)
  return ATTENDANCE_STATUS.PRESENT;
}

/**
 * Get attendance status label for display
 * @param {string|null} status - Attendance status
 * @returns {string} Human-readable label
 */
export function getAttendanceLabel(status) {
  if (!status) return '';
  return ATTENDANCE_LABELS[status] || status;
}

/**
 * Get attendance status colors for styling
 * @param {string|null} status - Attendance status
 * @returns {object} Color classes { bg, hover, text }
 */
export function getAttendanceColors(status) {
  if (!status) return { bg: 'bg-gray-300', hover: '', text: 'text-gray-600' };
  return ATTENDANCE_COLORS[status] || { bg: 'bg-gray-300', hover: '', text: 'text-gray-600' };
}

// =====================================================
// QUERIES
// =====================================================

/**
 * Get absences for a group on a specific date
 * Returns a map of child_id -> absence record
 * Note: This queries by child's group, not a group_id on absences table
 */
export const useGroupAbsencesMap = (groupId, date, options = {}) => {
  return useQuery({
    queryKey: ['absences', 'group-map', groupId, date],
    queryFn: async () => {
      // First get children in the group
      const { data: children, error: childrenError } = await supabase
        .from('children')
        .select('id')
        .eq('group_id', groupId);

      if (childrenError) throw childrenError;
      if (!children || children.length === 0) return {};

      const childIds = children.map(c => c.id);

      // Then get absences for those children on the date
      const { data, error } = await supabase
        .from('absences')
        .select('*')
        .in('child_id', childIds)
        .eq('absence_date', date);

      if (error) throw error;

      // Convert to map: child_id -> absence record
      const absenceMap = {};
      (data || []).forEach(absence => {
        absenceMap[absence.child_id] = absence;
      });

      return absenceMap;
    },
    enabled: !!groupId && !!date,
    ...options
  });
};

/**
 * Get absences for multiple children within a date range
 * Returns a map of `${child_id}_${date}` -> absence record
 */
export const useChildrenAbsencesMap = (childIds = [], startDate, endDate, options = {}) => {
  return useQuery({
    queryKey: ['absences', 'children-map', childIds, startDate, endDate],
    queryFn: async () => {
      if (!childIds || childIds.length === 0) return {};

      let query = supabase
        .from('absences')
        .select('*')
        .in('child_id', childIds);

      if (startDate) {
        query = query.gte('absence_date', startDate);
      }
      if (endDate) {
        query = query.lte('absence_date', endDate);
      }

      const { data, error } = await query;
      if (error) throw error;

      // Convert to map: `${child_id}_${absence_date}` -> absence record
      const absenceMap = {};
      (data || []).forEach(absence => {
        const key = `${absence.child_id}_${absence.absence_date}`;
        absenceMap[key] = absence;
      });

      return absenceMap;
    },
    enabled: childIds && childIds.length > 0,
    ...options
  });
};

/**
 * Hook to get derived attendance status for a group on a date
 * Combines daily_updates and absences to compute final status
 */
export const useGroupAttendanceStatus = (groupId, date, dailyUpdates = {}, options = {}) => {
  const { data: absencesMap = {}, isLoading, error } = useGroupAbsencesMap(groupId, date, options);

  // Compute derived status for each child
  const getStatusForChild = (childId) => {
    const dailyUpdate = dailyUpdates[childId];
    const absence = absencesMap[childId];
    return getAttendanceStatus(date, dailyUpdate, absence);
  };

  return {
    absencesMap,
    getStatusForChild,
    isLoading,
    error
  };
};
